import { shiftKeys, updateShiftTeam } from "@/api/Shifts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import styles from "./ShiftDetails.module.scss";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import Select from "@medoma/components/Select/Select";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useState } from "react";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { type IScheduledShift } from "@medoma/models/shifts";
import type { ITeam } from "@medoma/models/patients";

const NO_TEAM = 0;

export const ChangeTeam = ({
  shift,
  teams,
}: {
  shift: IScheduledShift;
  teams: ITeam[];
}) => {
  const { t } = useLingui();
  const shiftId = shift.id;

  const { register, handleSubmit } = useForm<{
    scheduledTeamId: ITeam["id"];
  }>({
    defaultValues: {
      scheduledTeamId: shift.team?.id ?? NO_TEAM,
    },
  });

  const [isChangingTeam, setIsChangingTeam] = useState(false);

  const queryClient = useQueryClient();

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const {
    mutate: mutateTeam,
    isPending: isMutatingTeam,
    variables: mutationVariables,
  } = useMutation({
    mutationFn: ({
      teamId,
      shiftId,
    }: {
      teamId: ITeam["id"];
      shiftId: IScheduledShift["id"];
    }) => {
      if (teamId === NO_TEAM) {
        return updateShiftTeam({ shiftId, teamId: null });
      }
      return updateShiftTeam({ shiftId, teamId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
      setIsChangingTeam(false);
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att genomföra ändringen`}. ${deducedError(error)}`,
      );
    },
  });

  const loadingMessage = isMutatingTeam
    ? mutationVariables?.teamId === NO_TEAM
      ? t`Tar bort team`
      : t`Ändrar team`
    : null;

  return (
    <>
      <div className={styles.actions}>
        {isChangingTeam ? (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingTeam(false);
            }}
            disabled={isMutatingTeam}
            aria-label={t`Avbryt ändring av team`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingTeam(true);
            }}
            disabled={isMutatingTeam}
            aria-label={shift.team ? t`Ändra team` : t`Lägg till team`}
          >
            {shift.team ? <Trans>Ändra</Trans> : <Trans>Lägg till</Trans>}
          </PlainButton>
        )}
      </div>
      {isChangingTeam ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(async (validatedFormData) => {
              mutateTeam({
                teamId: validatedFormData.scheduledTeamId,
                shiftId,
              });
            })}
          >
            {loadingMessage ? (
              <Loading message={loadingMessage} />
            ) : (
              <>
                <Select
                  label={t`Välj team`}
                  {...register("scheduledTeamId", { valueAsNumber: true })}
                >
                  <option value={NO_TEAM}>
                    <Trans>Inget</Trans>
                  </option>
                  {teams.map((team) => {
                    return (
                      <option value={team.id} key={team.id}>
                        {team.displayName}
                      </option>
                    );
                  })}
                </Select>
                <FilledButton disabled={isMutatingTeam} type="submit">
                  <Trans>Spara ändringar</Trans>
                </FilledButton>
              </>
            )}
          </Form>
        </div>
      ) : null}
    </>
  );
};
